import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, Calendar, Clock, User, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { useMasterData } from '../contexts/MasterDataContext';
import axios from 'axios';

import { BACKEND_URL, API } from '../utils/apiConfig';

const TIME_SLOTS = [
  '06:00 AM', '06:30 AM', '07:00 AM', '07:30 AM', '08:00 AM', '08:30 AM',
  '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM',
  '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM',
  '06:00 PM', '06:30 PM', '07:00 PM', '07:30 PM', '08:00 PM', '08:30 PM',
  '09:00 PM', '09:30 PM', '10:00 PM', '10:30 PM', '11:00 PM', '11:30 PM'
];

const GAME_COLORS = {
  'Pickleball': 'bg-green-500',
  'Badminton': 'bg-blue-500',
  'Cricket': 'bg-orange-500',
  'Football Half': 'bg-purple-500',
  'Football Full': 'bg-pink-500'
};

export default function CalendarViewPage() {
  const { getVenueNames, getGamesForVenue, getCourtsForVenueGame, loading: masterLoading } = useMasterData();
  const VENUES = getVenueNames();
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedVenue, setSelectedVenue] = useState('');
  const [selectedGame, setSelectedGame] = useState('all');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('day'); // 'day' or 'week'

  // Set default venue when master data loads
  useEffect(() => {
    if (VENUES.length > 0 && !selectedVenue) {
      setSelectedVenue(VENUES[0]);
    }
  }, [VENUES, selectedVenue]);

  // Safe access to games and courts - only after data is loaded
  const games = (selectedVenue && !masterLoading) ? (getGamesForVenue(selectedVenue) || []) : [];
  const courts = (selectedVenue && !masterLoading && selectedGame !== 'all')
    ? (getCourtsForVenueGame(selectedVenue, selectedGame) || [])
    : (selectedVenue && !masterLoading)
      ? games.flatMap(g => getCourtsForVenueGame(selectedVenue, g) || [])
      : [];

  useEffect(() => {
    fetchBookings();
  }, [selectedDate, selectedVenue, selectedGame, viewMode]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const startDate = new Date(selectedDate);
      const endDate = new Date(selectedDate);
      
      if (viewMode === 'week') {
        startDate.setDate(startDate.getDate() - startDate.getDay());
        endDate.setDate(startDate.getDate() + 6);
      }

      const params = new URLSearchParams({
        venue: selectedVenue,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      });
      
      if (selectedGame !== 'all') {
        params.append('game', selectedGame);
      }

      const response = await axios.get(`${API}/bookings?${params.toString()}`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else {
      newDate.setDate(newDate.getDate() + (direction * 7));
    }
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const getBookingsForSlot = (court, timeSlot, date = selectedDate) => {
    const dateStr = formatDateKey(date);
    return bookings.filter(b => 
      b.court === court && 
      b.date === dateStr &&
      b.start_time === timeSlot
    );
  };

  const getBookingStyle = (booking) => {
    const colorClass = GAME_COLORS[booking.game] || 'bg-slate-500';
    return colorClass;
  };

  const getWeekDates = () => {
    const dates = [];
    const start = new Date(selectedDate);
    start.setDate(start.getDate() - start.getDay());
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // Natural sort function for court names (Court-1, Court-2, ... Court-10, Court-11)
  const naturalSort = (a, b) => {
    // Extract numeric part if exists
    const extractNum = (str) => {
      const match = str.match(/(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    };
    
    // Extract prefix (e.g., "Court-", "A", "B-", "C")
    const extractPrefix = (str) => {
      return str.replace(/\d+/g, '').trim();
    };
    
    const prefixA = extractPrefix(a);
    const prefixB = extractPrefix(b);
    
    // First sort by prefix
    if (prefixA !== prefixB) {
      return prefixA.localeCompare(prefixB);
    }
    
    // Then sort by number
    return extractNum(a) - extractNum(b);
  };

  // Get courts sorted by game first, then by natural sort
  const getSortedCourts = () => {
    if (!selectedVenue || masterLoading) return [];
    
    if (selectedGame !== 'all') {
      // Single game selected - just natural sort the courts
      const gameCourts = getCourtsForVenueGame(selectedVenue, selectedGame) || [];
      return [...gameCourts].sort(naturalSort);
    }
    
    // All games - group by game, then sort within each game
    const courtsByGame = {};
    const gameOrder = ['Pickleball', 'Badminton', 'Cricket', 'Football Half', 'Football Full'];
    
    games.forEach(game => {
      const gameCourts = getCourtsForVenueGame(selectedVenue, game) || [];
      if (gameCourts.length > 0) {
        courtsByGame[game] = [...gameCourts].sort(naturalSort);
      }
    });
    
    // Flatten in game order
    const sortedCourts = [];
    gameOrder.forEach(game => {
      if (courtsByGame[game]) {
        sortedCourts.push(...courtsByGame[game]);
      }
    });
    
    // Remove duplicates while preserving order
    return [...new Set(sortedCourts)];
  };

  // Get unique courts from bookings for selected filters - use master data with proper sorting
  const displayCourts = getSortedCourts();

  // Show loading state while master data loads
  if (masterLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900">Calendar View</h1>
          <p className="text-slate-600 mt-1">Visual overview of all bookings across courts</p>
        </div>

        {/* Controls */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigateDate(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigateDate(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <span className="font-semibold text-lg ml-2">
                {viewMode === 'day' ? formatDate(selectedDate) : `Week of ${formatDate(getWeekDates()[0])}`}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <Select value={selectedVenue} onValueChange={setSelectedVenue}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select Venue" />
                </SelectTrigger>
                <SelectContent>
                  {VENUES.map(venue => (
                    <SelectItem key={venue} value={venue}>{venue}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedGame} onValueChange={setSelectedGame}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Select Game" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Games</SelectItem>
                  {games.map(game => (
                    <SelectItem key={game} value={game}>{game}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex border rounded-lg overflow-hidden">
                <Button 
                  variant={viewMode === 'day' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setViewMode('day')}
                  className="rounded-none"
                >
                  Day
                </Button>
                <Button 
                  variant={viewMode === 'week' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setViewMode('week')}
                  className="rounded-none"
                >
                  Week
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 justify-center">
          {Object.entries(GAME_COLORS).map(([game, color]) => (
            <div key={game} className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded ${color}`}></div>
              <span className="text-sm text-slate-600">{game}</span>
            </div>
          ))}
        </div>

        {/* Calendar Grid - Compact View */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : viewMode === 'day' ? (
          /* Day View - Compact with sticky headers */
          <Card className="overflow-hidden">
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <table className="w-full border-collapse text-xs">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 p-1 text-left w-16 sticky left-0 bg-slate-100 z-30 font-semibold">Time</th>
                    {displayCourts.length > 0 ? displayCourts.map(court => (
                      <th key={court} className="border border-slate-300 p-1 text-center min-w-[70px] max-w-[90px] font-semibold bg-slate-100">
                        <span className="block truncate">{court}</span>
                      </th>
                    )) : (
                      <th className="border border-slate-300 p-1 text-center bg-slate-100">No courts</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {TIME_SLOTS.map(timeSlot => (
                    <tr key={timeSlot} className="hover:bg-slate-50">
                      <td className="border border-slate-200 p-1 text-xs font-medium text-slate-600 sticky left-0 bg-white z-10 whitespace-nowrap">
                        {timeSlot}
                      </td>
                      {displayCourts.map(court => {
                        const slotBookings = getBookingsForSlot(court, timeSlot);
                        return (
                          <td key={`${court}-${timeSlot}`} className="border border-slate-200 p-0.5 h-8">
                            {slotBookings.map(booking => (
                              <div
                                key={booking.booking_id}
                                className={`${getBookingStyle(booking)} text-white text-[10px] px-1 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity`}
                                title={`${booking.player_name} - ${booking.game}\n${booking.start_time} - ${booking.end_time}\n₹${booking.amount}`}
                              >
                                <div className="font-medium truncate">{booking.player_name}</div>
                              </div>
                            ))}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          /* Week View - Compact with sticky headers */
          <Card className="overflow-hidden">
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <table className="w-full border-collapse text-xs">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 p-1 text-left w-20 sticky left-0 bg-slate-100 z-30 font-semibold">Court</th>
                    {getWeekDates().map(date => (
                      <th key={date.toISOString()} className="border border-slate-300 p-1 text-center min-w-[80px] bg-slate-100">
                        <div className={`${formatDateKey(date) === formatDateKey(new Date()) ? 'text-primary font-bold' : ''}`}>
                          {date.toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        <div className="text-sm font-semibold">{date.getDate()}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayCourts.map(court => (
                    <tr key={court} className="hover:bg-slate-50">
                      <td className="border border-slate-200 p-1 font-medium text-slate-700 sticky left-0 bg-white z-10 whitespace-nowrap">
                        {court}
                      </td>
                      {getWeekDates().map(date => {
                        const dayBookings = bookings.filter(b => 
                          b.court === court && b.date === formatDateKey(date)
                        );
                        return (
                          <td key={`${court}-${date.toISOString()}`} className="border border-slate-200 p-0.5 align-top">
                            <div className="space-y-0.5 max-h-20 overflow-y-auto">
                              {dayBookings.slice(0, 4).map(booking => (
                                <div
                                  key={booking.booking_id}
                                  className={`${getBookingStyle(booking)} text-white text-[9px] px-1 py-0.5 rounded cursor-pointer hover:opacity-80`}
                                  title={`${booking.player_name}\n${booking.start_time} - ${booking.end_time}`}
                                >
                                  <div className="truncate">{booking.start_time.replace(':00', '')}</div>
                                </div>
                              ))}
                              {dayBookings.length > 4 && (
                                <div className="text-[9px] text-slate-500 text-center">
                                  +{dayBookings.length - 4}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
